// Small date / currency / verdict formatters used across the SPA.

const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
const SHORT_DATE = new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });

export function formatCAD(n: number | null | undefined): string {
  return n == null ? '—' : CAD.format(n);
}

/** Compact money for cards / lists. "$1.2M", "$200K", "$0".
 *  Use this when a row must fit in one glance; use formatCAD() for
 *  the precise figure in detail views or audit-relevant contexts. */
export function formatCADCompact(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n === 0) return '$0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    return `${sign}$${m >= 10 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    return `${sign}$${k >= 10 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  return `${sign}$${abs}`;
}

/** Countdown ("in 12 days", "today", "5 days ago"). Returns null when
 *  no date supplied so callers can omit the chip cleanly. */
export function formatCountdown(ms: number | null | undefined): {
  text: string;
  tone: 'red' | 'amber' | 'neutral' | 'past';
} | null {
  if (!ms) return null;
  const days = Math.round((ms - Date.now()) / 86_400_000);
  if (days < 0) return { text: `${Math.abs(days)} days ago`, tone: 'past' };
  if (days === 0) return { text: 'today', tone: 'red' };
  if (days === 1) return { text: 'in 1 day', tone: 'red' };
  if (days <= 7) return { text: `in ${days} days`, tone: 'red' };
  if (days <= 14) return { text: `in ${days} days`, tone: 'amber' };
  if (days <= 30) return { text: `in ${days} days`, tone: 'neutral' };
  return { text: `in ${days} days`, tone: 'neutral' };
}

export const countdownToneCls: Record<'red' | 'amber' | 'neutral' | 'past', string> = {
  red: 'bg-rose-100 text-rose-900',
  amber: 'bg-amber-100 text-amber-900',
  neutral: 'bg-neutral-100 text-neutral-700',
  past: 'bg-neutral-100 text-neutral-500 line-through',
};

export type ProjectStatus = 'planning' | 'seeking' | 'funded' | 'complete' | 'cancelled';
export const projectStatusMeta: Record<ProjectStatus, { label: string; cls: string }> = {
  planning:  { label: 'Planning',  cls: 'bg-neutral-200 text-neutral-900' },
  seeking:   { label: 'Seeking',   cls: 'bg-sky-200 text-sky-900' },
  funded:    { label: 'Funded',    cls: 'bg-emerald-200 text-emerald-900' },
  complete:  { label: 'Complete',  cls: 'bg-[var(--color-accent)] text-white' },
  cancelled: { label: 'Archived',  cls: 'bg-neutral-200 text-neutral-600' },
};

/** Money-tone helpers: green for available, red for gap > 0, neutral 0. */
export function moneyToneCls(value: number, kind: 'gap' | 'committed' | 'available'): string {
  if (kind === 'gap') {
    if (value === 0) return 'text-neutral-700';
    return 'text-rose-700 font-medium';
  }
  if (kind === 'available') {
    if (value === 0) return 'text-neutral-700';
    return 'text-emerald-700 font-medium';
  }
  return 'text-neutral-900';
}

export function formatDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return SHORT_DATE.format(new Date(ms));
}

/** "Closes Jun 14 · 35 days" / "Closes Jun 14 · Today" / "Closed Jun 1" */
export function formatClosing(closesAtMs: number | null | undefined): string {
  if (!closesAtMs) return 'No deadline';
  const now = Date.now();
  const diffDays = Math.round((closesAtMs - now) / 86_400_000);
  const date = SHORT_DATE.format(new Date(closesAtMs));
  if (diffDays < 0) return `Closed ${date}`;
  if (diffDays === 0) return `Closes ${date} · today`;
  if (diffDays === 1) return `Closes ${date} · 1 day`;
  return `Closes ${date} · ${diffDays} days`;
}

export type Verdict = 'eligible' | 'likely_eligible' | 'unclear' | 'probably_not' | 'not_eligible';
export type MatchStatus = 'new' | 'watching' | 'pursuing' | 'dismissed' | 'escalated' | 'applied';

// Color choices respect BUILD.md §13.5 mappings but adjust shades so each
// foreground/background pair clears WCAG 2 AA 4.5:1 (verified via axe).
// "Probably not" gets dark text on a soft rose tint — the original
// rose-400 + white spec failed AA at 2.8:1.
export const verdictMeta: Record<Verdict, { label: string; cls: string }> = {
  eligible:        { label: 'Eligible',        cls: 'bg-emerald-700 text-white' },
  likely_eligible: { label: 'Likely',          cls: 'bg-amber-300 text-amber-950' },
  unclear:         { label: 'Unclear',         cls: 'bg-neutral-600 text-white' },
  probably_not:    { label: 'Probably not',    cls: 'bg-rose-200 text-rose-900' },
  not_eligible:    { label: 'Not eligible',    cls: 'bg-rose-700 text-white' },
};

// Color choices chosen so that the foreground/background pairs all clear
// WCAG 2 AA's 4.5:1 ratio for normal text (verified via axe-core).
export const statusMeta: Record<MatchStatus, { label: string; cls: string }> = {
  new:       { label: 'New',       cls: 'bg-neutral-200 text-neutral-900' },
  watching:  { label: 'Watching',  cls: 'bg-sky-200 text-sky-900' },
  pursuing:  { label: 'Pursuing',  cls: 'bg-emerald-200 text-emerald-900' },
  dismissed: { label: 'Dismissed', cls: 'bg-neutral-200 text-neutral-700' },
  escalated: { label: 'Escalated', cls: 'bg-amber-200 text-amber-900' },
  applied:   { label: 'Applied',   cls: 'bg-[var(--color-accent)] text-white' },
};

export function fitPct(score: number | null | undefined): string {
  if (score == null) return '—';
  return `${Math.round(score * 100)}%`;
}

export type AppStage = 'drafting' | 'submitted' | 'awaiting' | 'won' | 'lost' | 'withdrawn';

// Visible kanban stages, in column order.
export const KANBAN_STAGES: ReadonlyArray<Extract<AppStage, 'drafting' | 'submitted' | 'awaiting' | 'won' | 'lost'>> = [
  'drafting', 'submitted', 'awaiting', 'won', 'lost',
];

export const stageMeta: Record<AppStage, { label: string; cls: string; columnCls: string }> = {
  drafting:  { label: 'Drafting',  cls: 'bg-neutral-200 text-neutral-900', columnCls: 'bg-neutral-50 border-neutral-200' },
  submitted: { label: 'Submitted', cls: 'bg-sky-200 text-sky-900',         columnCls: 'bg-sky-50 border-sky-200' },
  awaiting:  { label: 'Awaiting',  cls: 'bg-amber-200 text-amber-900',     columnCls: 'bg-amber-50 border-amber-200' },
  won:       { label: 'Won',       cls: 'bg-emerald-700 text-white',       columnCls: 'bg-emerald-50 border-emerald-200' },
  lost:      { label: 'Lost',      cls: 'bg-rose-200 text-rose-900',       columnCls: 'bg-rose-50 border-rose-200' },
  withdrawn: { label: 'Withdrawn', cls: 'bg-neutral-300 text-neutral-800', columnCls: 'bg-neutral-100 border-neutral-300' },
};

// Finance status → AA-compliant text/background pairs. Ratios verified.
// green/amber/red are utilisation buckets, not the stage colour palette.
export const exposureStatusMeta: Record<'green' | 'amber' | 'red' | 'unset', { label: string; barCls: string; chipCls: string }> = {
  green: { label: 'Within ceiling',     barCls: 'bg-emerald-700',      chipCls: 'bg-emerald-100 text-emerald-900' },
  amber: { label: 'Approaching ceiling', barCls: 'bg-amber-500',        chipCls: 'bg-amber-100 text-amber-900' },
  red:   { label: 'Over ceiling',        barCls: 'bg-rose-700',         chipCls: 'bg-rose-100 text-rose-900' },
  unset: { label: 'No ceiling set',      barCls: 'bg-neutral-400',      chipCls: 'bg-neutral-200 text-neutral-800' },
};
